import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { attachAuthInterceptors } from '../utils/axiosAuth';
import {
  Facility,
  CreateFacilityRequest,
  UpdateFacilityRequest,
  FacilityResponse,
  FacilitiesListResponse,
  FacilityQueryParams,
  FacilityStatistics,
  FacilityAssessmentStats,
  FacilityStatsResponse,
  FacilityAssessmentStatsResponse,
  PaginationInfo
} from '../types/facility';

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5002/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          console.error('Bad Request:', (data as any)?.message);
          break;
        case 401:
          console.error('Unauthorized:', (data as any)?.message);
          break;
        case 404:
          console.error('Not Found:', (data as any)?.message);
          break;
        case 500:
          console.error('Server Error:', (data as any)?.message);
          break;
        default:
          console.error('API Error:', (data as any)?.message);
      }
    } else if (error.request) {
      console.error('Network Error:', 'Unable to connect to server');
    } else {
      console.error('Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

attachAuthInterceptors(api);

// Facility API service functions
export const facilityAPI = {
  // Create a new facility
  createFacility: async (facilityData: CreateFacilityRequest): Promise<Facility> => {
    try {
      const response = await api.post('/facilities', facilityData);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all facilities with optional filters
  getFacilities: async (params: FacilityQueryParams = {}): Promise<{ facilities: Facility[]; total: number; pagination: PaginationInfo }> => {
    try {
      const response = await api.get('/facilities', { params });
      return {
        facilities: response.data.data || [],
        total: response.data.pagination?.totalItems || 0,
        pagination: response.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        }
      };
    } catch (error) {
      throw error;
    }
  },

  // Get a specific facility by ID
  getFacility: async (id: string): Promise<Facility> => {
    try {
      const response = await api.get(`/facilities/${id}`);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Update a facility
  updateFacility: async (id: string, facilityData: Partial<CreateFacilityRequest>): Promise<Facility> => {
    try {
      const response = await api.put(`/facilities/${id}`, facilityData);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete a facility
  deleteFacility: async (id: string): Promise<{ message: string }> => {
    try {
      const response = await api.delete(`/facilities/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get facility statistics
  getFacilityStatistics: async (): Promise<FacilityStatistics> => {
    try {
      const response = await api.get('/facilities/statistics');
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get assessment statistics for a specific facility
  getFacilityAssessmentStats: async (facilityId: string): Promise<FacilityAssessmentStats> => {
    try {
      const response = await api.get(`/facilities/${facilityId}/assessment-stats`);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Search facilities
  searchFacilities: async (query: string): Promise<Facility[]> => {
    try {
      const response = await api.get('/facilities/search', { 
        params: { q: query } 
      });
      return response.data.data || [];
    } catch (error) {
      throw error;
    }
  },

  // Get facilities by state
  getFacilitiesByState: async (state: string): Promise<Facility[]> => {
    try {
      const response = await api.get('/facilities/by-state', { 
        params: { state } 
      });
      return response.data.data || [];
    } catch (error) {
      throw error;
    }
  },

  // Get facilities by type
  getFacilitiesByType: async (type: string): Promise<Facility[]> => {
    try {
      const response = await api.get('/facilities/by-type', { 
        params: { type } 
      });
      return response.data.data || [];
    } catch (error) {
      throw error;
    }
  },

  // Bulk operations
  bulkUpdateFacilities: async (updates: Array<{ id: string; data: Partial<CreateFacilityRequest> }>): Promise<Facility[]> => {
    try {
      const response = await api.put('/facilities/bulk-update', { updates });
      return response.data.data || [];
    } catch (error) {
      throw error;
    }
  },

  // Export facilities data
  exportFacilities: async (params: FacilityQueryParams = {}): Promise<Blob> => {
    try {
      const response = await api.get('/facilities/export', { 
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default api;
